import { useNavigate, useSearchParams } from "react-router-dom";
import { useMemo } from "react";
import type { AgeRating, GenreKey } from "./data";

export type Route =
  | { page: "home" }
  | { page: "work"; id: string }
  | { page: "author"; id?: string }
  | {
      page: "search";
      query?: string;
      /** 単数指定(後方互換用)。複数選択は genres を使う */
      genre?: GenreKey;
      genres?: GenreKey[];
      tag?: string;
      tags?: string[];
      prices?: string[];
      ages?: AgeRating[];
      sort?: SortKey;
    }
  | { page: "library" }
  | { page: "dashboard" }
  | { page: "auth"; mode?: "login" | "signup" };

export type SortKey = "popular" | "new" | "rating" | "price-asc";

export type Nav = (route: Route) => void;

export interface ToastInfo {
  kind: "info" | "success" | "warn";
  text: string;
}

/**
 * 判別型の Route を URL に変換する。
 * (Nav の呼び出しを既存のページ実装から書き換えずに済むようにラップ)
 */
export function routeToPath(route: Route): string {
  switch (route.page) {
    case "home":
      return "/";
    case "work":
      return `/works/${encodeURIComponent(route.id)}`;
    case "author":
      return route.id ? `/users/${encodeURIComponent(route.id)}` : "/users/me";
    case "search": {
      const sp = new URLSearchParams();
      if (route.query) sp.set("q", route.query);
      const genres = route.genres ?? (route.genre ? [route.genre] : []);
      if (genres.length > 0) sp.set("genres", genres.join(","));
      const tags = route.tags ?? (route.tag ? [route.tag] : []);
      if (tags.length > 0) sp.set("tags", tags.join(","));
      if (route.prices && route.prices.length > 0) sp.set("prices", route.prices.join(","));
      if (route.ages && route.ages.length > 0) sp.set("ages", route.ages.join(","));
      if (route.sort) sp.set("sort", route.sort);
      const q = sp.toString();
      return q ? `/search?${q}` : "/search";
    }
    case "library":
      return "/library";
    case "dashboard":
      return "/dashboard";
    case "auth":
      return route.mode === "signup" ? "/signup" : "/login";
  }
}

/** useNav: 既存ページから `nav({ page: ... })` を URL ベース遷移に変える */
export function useNav(): Nav {
  const navigate = useNavigate();
  return useMemo<Nav>(() => (route) => navigate(routeToPath(route)), [navigate]);
}

function parseList(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** ?q=&genres=&tags=&prices=&ages=&sort= をパース(複数値はカンマ区切り)。
 *  旧形式 ?genre= / ?tag= もフォールバックで受け付ける。 */
export function useSearchRoute(): Extract<Route, { page: "search" }> {
  const [sp] = useSearchParams();
  return useMemo(() => {
    const genres = parseList(sp.get("genres")) as GenreKey[];
    if (genres.length === 0 && sp.get("genre")) genres.push(sp.get("genre") as GenreKey);
    const tags = parseList(sp.get("tags"));
    if (tags.length === 0 && sp.get("tag")) tags.push(sp.get("tag")!);
    return {
      page: "search",
      ...(sp.get("q") ? { query: sp.get("q")! } : {}),
      ...(genres.length > 0 ? { genres } : {}),
      ...(tags.length > 0 ? { tags } : {}),
      ...(parseList(sp.get("prices")).length > 0 ? { prices: parseList(sp.get("prices")) } : {}),
      ...(parseList(sp.get("ages")).length > 0
        ? { ages: parseList(sp.get("ages")) as AgeRating[] }
        : {}),
      ...(sp.get("sort") ? { sort: sp.get("sort") as SortKey } : {}),
    };
  }, [sp]);
}
